import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { AxiosError } from 'axios'
import { Building2 } from 'lucide-react'
import { cn } from '../UI/cn'
import {
  getLatestActionSummary,
  applyPreparedAction,
  ActionSummaryData,
} from '../../services/recommendations_api'
import type {
  EnrichedAction,
  PreparedAction,
} from '../../services/recommendations_contracts'
import { EnrichedActionCard } from './EnrichedActionCard'
import { SummaryStatsHeader } from './SummaryStatsHeader'
import { SIGNAL_SECTIONS, URGENCY_ICONS, URGENCY_COLORS } from './constants'
import useApiService from '../../services/api'
import { useRecommendationsPolling } from './useRecommendationsPolling'
import { useActionClickManager } from './useActionClickManager'
import { useCompletionPolling } from './useCompletionPolling'
import {
  groupActionsByCompany,
  groupActionsBySignal,
  partitionActionsByStatus,
  sortByUrgencyScore,
} from './recommendationVisibility'

type ViewMode = 'priority' | 'signal' | 'company'

const URGENCY_BANDS: Array<{
  key: EnrichedAction['urgency']
  label: string
}> = [
  { key: 'do_now', label: 'Hot' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
]

interface RecommendationsCardProps {
  className?: string
  /** Optional user ID to view another user's summary (superusers only) */
  userId?: number
  /** Callback fired whenever the summary changes */
  onSummaryLoaded?: (summary: ActionSummaryData | null) => void
}

/**
 * Displays AI-generated daily recommendations for the current user.
 *
 * Shows prioritized action items grouped by urgency (default), signal type, or company.
 * All three views share the same flat design language: inline group headers + card lists.
 *
 * Superusers can pass a userId prop to view another user's summary.
 */
export const RecommendationsCard: React.FC<RecommendationsCardProps> = ({
  className,
  userId,
  onSummaryLoaded,
}) => {
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<ViewMode>('priority')
  const [showResolved, setShowResolved] = useState(false)
  const { data: companyData } = useApiService().useGetCompanyDetails()
  const enabledSignalTypes = companyData?.enabled_signal_types
  const filteredSignalSections = useMemo(
    () =>
      enabledSignalTypes
        ? SIGNAL_SECTIONS.filter((s) => enabledSignalTypes.includes(s.key))
        : SIGNAL_SECTIONS,
    [enabledSignalTypes]
  )
  const viewTabs = useMemo(
    () => [
      { key: 'priority' as ViewMode, title: 'Priority' },
      { key: 'signal' as ViewMode, title: 'Type' },
      { key: 'company' as ViewMode, title: 'Company' },
    ],
    []
  )

  const summaryQuery = useQuery<ActionSummaryData | null, Error>({
    queryKey: ['recommendations', userId ?? 'self'],
    queryFn: async () => {
      try {
        return await getLatestActionSummary(userId)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          axiosError.response?.data?.detail ||
            axiosError.message ||
            'Failed to fetch recommendations'
        )
      }
    },
    refetchOnWindowFocus: false,
  })

  const summary = summaryQuery.data

  // Polling for GENERATING status
  useRecommendationsPolling({
    summary,
    refetch: summaryQuery.refetch,
    userId,
  })

  // On load + every 2 min: check whether any OPEN clicks are now done in the CRM.
  // Disabled when viewing another user's summary (superuser mode) since the endpoint
  // always targets the authenticated user.
  useCompletionPolling({
    queryKey: ['recommendations', userId ?? 'self'],
    enabled: userId === undefined,
  })

  useEffect(() => {
    onSummaryLoaded?.(summary ?? null)
  }, [summary, onSummaryLoaded])

  useEffect(() => {
    setShowResolved(false)
  }, [summary?.id, userId])

  // Action click management
  const {
    handleActionClick,
    handleStatusClick,
    getStatusForAction,
    getAutoCompletedForAction,
  } = useActionClickManager({ summary })

  const handleApplyPreparedAction = async (
    action: EnrichedAction,
    preparedAction: PreparedAction
  ) => {
    if (preparedAction.type !== 'update_stage') return
    try {
      await applyPreparedAction(action.deal_id, preparedAction.target_stage_id)
    } catch {
      // applyPreparedAction already shows an error toast via baseAPICall.
      // Bail out so the click is not incorrectly marked done.
      return
    }
    queryClient.invalidateQueries({
      queryKey: ['deal', String(action.deal_id)],
    })
    await handleStatusClick(action, 'done', false)
    await summaryQuery.refetch()
  }

  const allActions = summary?.summary_data?.enriched_actions ?? []
  const { activeActions, resolvedActions } = useMemo(
    () => partitionActionsByStatus(allActions, getStatusForAction),
    [allActions, getStatusForAction]
  )

  const actionsByUrgency = useMemo(
    () => sortByUrgencyScore(activeActions),
    [activeActions]
  )
  const resolvedByUrgency = useMemo(
    () => sortByUrgencyScore(resolvedActions),
    [resolvedActions]
  )
  const actionsBySignal = useMemo(
    () => groupActionsBySignal(activeActions),
    [activeActions]
  )
  const resolvedActionsBySignal = useMemo(
    () => groupActionsBySignal(resolvedActions),
    [resolvedActions]
  )
  const actionsByCompany = useMemo(
    () => groupActionsByCompany(activeActions),
    [activeActions]
  )
  const resolvedActionsByCompany = useMemo(
    () => groupActionsByCompany(resolvedActions),
    [resolvedActions]
  )

  if (summaryQuery.isLoading) {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface shadow-sm overflow-hidden',
          className
        )}
      >
        <div className="bg-surface-alt px-4 py-4 space-y-3 animate-pulse">
          {/* Stats header placeholder */}
          <div className="flex items-center gap-4">
            <div className="h-4 w-24 rounded bg-surface-disabled" />
            <div className="h-4 w-16 rounded bg-surface-disabled" />
            <div className="h-4 w-16 rounded bg-surface-disabled" />
          </div>
          {/* Card placeholders */}
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-lg border border-line bg-surface p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-surface-disabled flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-1/3 rounded bg-surface-disabled" />
                  <div className="h-3 w-1/2 rounded bg-surface-disabled" />
                  <div className="h-3 w-full rounded bg-surface-disabled" />
                  <div className="h-3 w-5/6 rounded bg-surface-disabled" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const queryError = summaryQuery.error as Error | undefined

  if (queryError) {
    return (
      <div
        className={cn(
          'rounded-lg border border-error-border bg-error-bg p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-error">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">Error loading recommendations</span>
        </div>
        <p className="mt-2 text-sm text-error">{queryError.message}</p>
      </div>
    )
  }

  // No summary exists yet (status is null)
  if (!summary || summary.status === null) {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface p-6 shadow-sm',
          className
        )}
      >
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-fg-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-fg">
            No recommendations available
          </h3>
          <p className="mt-1 text-sm text-fg-secondary">
            Click &quot;Generate Recommendations&quot; to create your daily
            recommendations
          </p>
        </div>
      </div>
    )
  }

  if (summary.status === 'FAILED') {
    return (
      <div
        className={cn(
          'rounded-lg border border-warning-border bg-warning-bg p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-warning">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-medium">Summary generation failed</span>
        </div>
        {summary.error_message && (
          <p className="mt-2 text-sm text-warning">{summary.error_message}</p>
        )}
      </div>
    )
  }

  if (summary.status === 'GENERATING') {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-fg-tertiary">
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="font-medium">
            Generating your recommendations...
          </span>
        </div>
        <p className="mt-2 text-sm text-fg-secondary">
          This usually takes 10-30 seconds
        </p>
      </div>
    )
  }

  const hasActions = allActions.length > 0
  const hasActiveActions = activeActions.length > 0
  const hasResolvedActions = resolvedActions.length > 0
  const hasSignalData =
    Object.keys(actionsBySignal).length > 0 ||
    (showResolved && Object.keys(resolvedActionsBySignal).length > 0)

  /** Shared empty state shown when no active actions exist in any view. */
  const emptyState = (
    <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-5 text-center">
      <p className="text-sm font-medium text-fg">No active recommendations</p>
      <p className="mt-1 text-sm text-fg-secondary">
        {hasResolvedActions
          ? 'Resolved items are hidden from your active queue.'
          : 'Your pipeline looks healthy.'}
      </p>
    </div>
  )

  /** Shared resolved section header. */
  const resolvedHeader = (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-fg">Resolved</h3>
      <span className="text-xs text-fg-muted">Done and dismissed items</span>
    </div>
  )

  /**
   * Render a group header matching the priority-view band style:
   * icon + uppercase label + muted count.
   */
  const renderGroupHeader = (
    Icon: React.ElementType,
    label: string,
    _count: number,
    iconClass: string
  ) => (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={13} className={iconClass} />
      <span
        className={`text-xs font-semibold uppercase tracking-wide ${iconClass}`}
      >
        {label}
      </span>
    </div>
  )

  /**
   * Render a list of action cards for a group.
   */
  const renderCards = (actions: EnrichedAction[], keyPrefix: string) => (
    <div className="space-y-2">
      {actions.map((action, index) => (
        <EnrichedActionCard
          key={`${keyPrefix}-${action.signal_fingerprint ?? `${action.deal_id}-${index}`}`}
          action={action}
          onCardClick={() =>
            handleActionClick(action, action.signal_type, action.action_type)
          }
          onStatusClick={(status) => handleStatusClick(action, status)}
          actionStatus={getStatusForAction(action)}
          actionAutoCompleted={getAutoCompletedForAction(action)}
          onApplyPreparedAction={
            (action.prepared_actions?.length ?? 0) > 0
              ? (pa) => handleApplyPreparedAction(action, pa)
              : undefined
          }
        />
      ))}
    </div>
  )

  return (
    <div
      className={cn(
        'rounded-lg border border-line bg-surface shadow-sm overflow-hidden',
        className
      )}
    >
      {hasActions ? (
        <div className="bg-surface-alt px-4 py-4 space-y-4">
          {summary.summary_data && (
            <SummaryStatsHeader
              data={summary.summary_data}
              visibleActions={activeActions}
              viewMode={viewMode}
              companiesCount={actionsByCompany.length}
            />
          )}

          {(hasSignalData || hasResolvedActions) && (
            <div className="flex items-center justify-end gap-3">
              {hasResolvedActions && (
                <button
                  type="button"
                  onClick={() => setShowResolved((prev) => !prev)}
                  className="inline-flex items-center rounded-full border border-line-subtle bg-surface px-3 py-1 text-xs font-medium text-fg-secondary transition-colors hover:border-line hover:text-fg"
                >
                  {showResolved
                    ? 'Hide resolved'
                    : `Show resolved (${resolvedActions.length})`}
                </button>
              )}
              <div className="flex items-center border border-line rounded-md overflow-hidden bg-surface flex-shrink-0">
                {viewTabs.map((tab, i) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setViewMode(tab.key)}
                    className={cn(
                      'px-3 py-1.5 text-xs transition-colors',
                      i > 0 && 'border-l border-line',
                      viewMode === tab.key
                        ? 'bg-surface-alt text-fg font-semibold'
                        : 'text-fg-secondary hover:bg-surface-hover'
                    )}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Priority view ── */}
          {viewMode === 'priority' && (
            <div className="space-y-4">
              {hasActiveActions ? (
                <div className="space-y-4">
                  {URGENCY_BANDS.map(({ key, label }) => {
                    const UrgencyIcon = URGENCY_ICONS[key]
                    const bandActions = actionsByUrgency.filter(
                      (a) => a.urgency === key
                    )
                    if (bandActions.length === 0) return null
                    return (
                      <div key={key}>
                        {renderGroupHeader(
                          UrgencyIcon,
                          label,
                          bandActions.length,
                          URGENCY_COLORS[key]
                        )}
                        {renderCards(bandActions, `priority-${key}`)}
                      </div>
                    )
                  })}
                </div>
              ) : (
                emptyState
              )}
              {showResolved && hasResolvedActions && (
                <div className="space-y-4">
                  {resolvedHeader}
                  {renderCards(resolvedByUrgency, 'priority-resolved')}
                </div>
              )}
            </div>
          )}

          {/* ── Signal type view ── */}
          {viewMode === 'signal' && (
            <div className="space-y-4">
              {hasActiveActions ? (
                <div className="space-y-4">
                  {filteredSignalSections.map(
                    ({ key, title, Icon, iconColor }) => {
                      const sectionActions = actionsBySignal[key] ?? []
                      if (sectionActions.length === 0) return null
                      return (
                        <div key={key}>
                          {renderGroupHeader(
                            Icon,
                            title,
                            sectionActions.length,
                            iconColor
                          )}
                          {renderCards(sectionActions, `signal-${key}`)}
                        </div>
                      )
                    }
                  )}
                </div>
              ) : (
                emptyState
              )}
              {showResolved && hasResolvedActions && (
                <div className="space-y-4">
                  {resolvedHeader}
                  <div className="space-y-4">
                    {filteredSignalSections.map(
                      ({ key, title, Icon, iconColor }) => {
                        const sectionActions =
                          resolvedActionsBySignal[key] ?? []
                        if (sectionActions.length === 0) return null
                        return (
                          <div key={`resolved-${key}`}>
                            {renderGroupHeader(
                              Icon,
                              title,
                              sectionActions.length,
                              iconColor
                            )}
                            {renderCards(
                              sectionActions,
                              `signal-resolved-${key}`
                            )}
                          </div>
                        )
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Company view ── */}
          {viewMode === 'company' && (
            <div className="space-y-4">
              {hasActiveActions ? (
                <div className="space-y-4">
                  {actionsByCompany.map((company) => (
                    <div key={company.companyId}>
                      {renderGroupHeader(
                        Building2,
                        company.companyName,
                        company.actions.length,
                        'text-fg-secondary'
                      )}
                      {renderCards(
                        company.actions,
                        `company-${company.companyId}`
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                emptyState
              )}
              {showResolved && hasResolvedActions && (
                <div className="space-y-4">
                  {resolvedHeader}
                  <div className="space-y-4">
                    {resolvedActionsByCompany.map((company) => (
                      <div key={`resolved-${company.companyId}`}>
                        {renderGroupHeader(
                          Building2,
                          company.companyName,
                          company.actions.length,
                          'text-fg-secondary'
                        )}
                        {renderCards(
                          company.actions,
                          `company-resolved-${company.companyId}`
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 text-center">
          <p className="text-fg-secondary">
            No actions found. Your pipeline looks healthy!
          </p>
        </div>
      )}

      {/* Generation duration footer */}
      {summary.created && (
        <div className="px-4 py-2 border-t border-line-subtle flex items-center justify-between">
          {summary.summary_data?.snoozed_count != null &&
            summary.summary_data.snoozed_count > 0 && (
              <span className="text-xs text-fg-muted">
                {summary.summary_data.snoozed_count} snoozed signal
                {summary.summary_data.snoozed_count !== 1 ? 's' : ''}
              </span>
            )}
          <span className="text-xs text-fg-muted ml-auto">
            Updated{' '}
            {new Date(summary.created).toLocaleDateString([], {
              month: 'short',
              day: 'numeric',
            })}{' '}
            {new Date(summary.created).toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>
      )}
    </div>
  )
}
